import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Button } from "@mui/material";
import { questions } from "./Quiz.tsx";
import { colors, fonts, shadows, radii, compactCardBreakpoint, shortViewportBreakpoint } from "../theme/tokens.ts";

interface LandingPageProps {
  onStartQuiz: () => void;
}

const guidelines = [
  `El test consta de ${questions.length} preguntas y la media de tiempo para ` +
    "contestarlas es de un máximo de 5 minutos. Tomate el tiempo que " +
    "necesites, prestando atención a las preguntas.",
  "Sé sincero/a a la hora de responder. Nadie te juzgará.",
  "Lo importante es poner lo que sientes, para que este recorrido te resulte útil.",
  "Al finalizar el test recibirás un mail con las respuestas de El Mapa de tu Vida: podrás auto observarte, ver de dónde vienes, dónde estás y hacia dónde quieres ir.",
];

//eslint-disable-next-line
const LandingPage: React.FC<LandingPageProps> = ({ onStartQuiz }) => {
  const navigate = useNavigate();
  const [lastResultsPath, setLastResultsPath] = useState<string | null>(null);

  useEffect(() => {
    // Verificar si hay una ruta de results guardada en localStorage
    const savedPath = localStorage.getItem("lastResultsPath");
    if (savedPath && savedPath.startsWith("/results/")) {
      setLastResultsPath(savedPath);
    }
  }, []);

  const handleBackToResults = () => {
    if (lastResultsPath) {
      navigate(lastResultsPath);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "6px 0 0",
        // Mismo criterio que en la pantalla de resultados: alto fijo y sin
        // scroll propio del documento (el embed real usa un iframe con
        // height:100vh sin auto-resize), para que un swipe no pueda
        // "escaparse" hacia la página de WordPress. Acá adentro, lo único
        // que scrollea es la tarjeta "Antes de comenzar".
        //
        // OJO: sin "flex:1" a propósito. Con flex:1 (que fija flex-basis:0%)
        // el box terminaba tomando el alto de su contenido en vez de
        // 100vh cuando el contenido no entraba, desbordando en pantallas
        // bajas (ej. 1920x480). height:100vh ya alcanza.
        height: "100vh",
        "@supports (height: 100dvh)": { height: "100dvh" },
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          maxWidth: 640,
          width: "100%",
          height: "100%",
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "16px",
          overflow: "hidden",
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              alignItems: "center",
              columnGap: "8px",
              rowGap: "6px",
            }}
          >
            <Typography
              component="h1"
              sx={{
                fontFamily: fonts.heading,
                fontWeight: 800,
                fontSize: { xs: "20px", md: "24px" },
                lineHeight: 1.15,
                color: colors.teal,
                m: 0,
              }}
            >
              Test <Box component="span" sx={{ color: colors.coral }}>El Mapa De Tu Vida</Box>
            </Typography>
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                background: colors.teal,
                color: "#ffffff",
                fontFamily: fonts.heading,
                fontWeight: 700,
                fontSize: "10px",
                letterSpacing: "0.6px",
                textTransform: "uppercase",
                padding: "4px 10px",
                borderRadius: `${radii.pill}px`,
                boxShadow: shadows.card,
                whiteSpace: "nowrap",
              }}
            >
              gratis
            </Box>
          </Box>
          <Typography
            sx={{
              mt: "8px",
              fontFamily: fonts.body,
              fontSize: "13px",
              lineHeight: 1.5,
              color: colors.muted,
              maxWidth: 640,
            }}
          >
            Un recorrido breve para mirar de frente tu mapa personal de fortalezas y áreas de mejora.
            Un punto de partida para tu orientación.
          </Typography>
        </Box>

        {/*
          Wrapper transparente con el scroll: si "Antes de comenzar" no entra
          en el espacio disponible, scrollea acá adentro, acotado, sin
          arrastrar al resto del layout ni escaparse hacia la página que
          embebe la app. En pantallas chicas (≤400x900) se le pone además un
          tope de alto para que la tarjeta quede más compacta.
        */}
        <Box
          sx={{
            // Mismo criterio que en resultados: solo crece (flexGrow) en
            // mobile. En desktop no debe crecer, para que lo que sigue
            // después de la tarjeta no quede empujado al borde inferior de
            // la pantalla. minHeight:0 sí hace falta siempre, para que si el
            // contenido no entra, se achique y scrollee acá adentro.
            flexGrow: { xs: 1, md: 0 },
            minHeight: 0,
            width: "100%",
            overflowY: "auto",
            overscrollBehavior: "contain",
            [shortViewportBreakpoint]: {
              maxHeight: "40vh",
            },
            [compactCardBreakpoint]: {
              maxHeight: "45vh",
            },
          }}
        >
          <Box
            sx={{
              background: "#ffffff",
              borderRadius: radii.card,
              padding: { xs: "14px", md: "18px" },
              width: "100%",
              boxShadow: shadows.card,
              textAlign: "left",
              display: "flex",
              flexDirection: "column",
              gap: "10px",
            }}
          >
            <Typography
              sx={{
                fontFamily: fonts.heading,
                fontWeight: 700,
                fontSize: "14px",
                color: colors.teal,
              }}
            >
              Antes de comenzar
            </Typography>

            <Typography
              sx={{
                fontFamily: fonts.body,
                fontSize: "12px",
                color: colors.body,
                mt: "-4px",
              }}
            >
              Es muy importante que leas los siguientes puntos para realizar
              bien el test:
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {guidelines.map((guideline, i) => (
                <Box
                  key={i}
                  sx={{ display: "flex", alignItems: "flex-start", gap: "8px" }}
                >
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      borderRadius: radii.swatch,
                      background:
                        i % 2 === 0
                          ? colors.featureSwatchCoral
                          : colors.featureSwatchTeal,
                      flexShrink: 0,
                      mt: "1px",
                    }}
                  />
                  <Typography
                    sx={{ fontFamily: fonts.body, fontSize: "12px", lineHeight: 1.5, color: colors.body }}
                  >
                    {guideline}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Typography
              sx={{
                fontFamily: fonts.heading,
                fontWeight: 700,
                fontSize: "12.5px",
                color: colors.coral,
                borderTop: `1px solid ${colors.borderFaint}`,
                pt: "8px",
                textAlign: "center",
              }}
            >
              ¡Adelante!
            </Typography>

            <Button
              onClick={onStartQuiz}
              disableRipple
              sx={{
                mt: "2px",
                alignSelf: "center",
                background: colors.coral,
                color: "#ffffff",
                borderRadius: `${radii.pill}px`,
                padding: "9px 18px",
                fontFamily: fonts.heading,
                fontWeight: 600,
                fontSize: "12px",
                textTransform: "none",
                "&:hover": { background: colors.coralHover },
              }}
            >
              Comenzar test →
            </Button>
          </Box>
        </Box>

        <Typography
          sx={{ fontFamily: fonts.body, fontSize: "11px", color: colors.faint, flexShrink: 0 }}
        >
          Tiempo estimado: hasta 5 minutos &nbsp;·&nbsp; Respuestas confidenciales
        </Typography>

        {lastResultsPath && (
          <Button
            onClick={handleBackToResults}
            disableRipple
            sx={{
              position: "fixed",
              bottom: { xs: 12, md: 16 },
              right: { xs: 12, md: 16 },
              background: colors.teal,
              color: "#ffffff",
              borderRadius: `${radii.pill}px`,
              padding: "8px 16px",
              fontFamily: fonts.heading,
              fontWeight: 600,
              fontSize: "11.5px",
              textTransform: "none",
              boxShadow: shadows.card,
              zIndex: 1000,
              "&:hover": { background: colors.tealBorder },
            }}
          >
            Volver a mis resultados
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default LandingPage;
